import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

/**
 * Quiet scroll hint under the privacy line on the guest /write landing.
 *
 * The composer keeps absolute first-screen dominance; this is the last,
 * smallest thing before the fold. It appears only after the composer has
 * settled, invites the curious without pulling the ready-to-type visitor
 * away from the input, and on click hands off to the parent, which
 * smooth-scrolls the manuscript proof into view and replays it from the
 * first scene.
 *
 * Desktop only (the parent renders it at `sm` and above, matching the full
 * proof card); on mobile the proof strip already sits under the composer.
 */
interface SeeHowItWorksProps {
  onClick: () => void;
}

const SeeHowItWorks: React.FC<SeeHowItWorksProps> = ({ onClick }) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.button
      type="button"
      onClick={onClick}
      aria-label="See how PaperVerse Writer works"
      className="flex flex-col items-center gap-1 rounded-full p-2 text-muted-foreground/80 transition-colors hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        transition: prefersReducedMotion
          ? { duration: 0.2 }
          : { delay: 1.2, duration: 0.5, ease: 'easeOut' },
      }}
      exit={{ opacity: 0, transition: { duration: 0.3 } }}
    >
      <span className="text-xs font-medium">See how it works</span>
      <motion.span
        animate={prefersReducedMotion ? {} : { y: [0, 6, 0] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
      >
        <ChevronDown className="h-4 w-4" />
      </motion.span>
    </motion.button>
  );
};

export default SeeHowItWorks;
